import React, { useState, useRef } from 'react';
import { Card, CardContent } from '../../../components/tailwind/card';
import { Button } from '../../../components/tailwind/button';
import { Sparkles, Wand2, Loader2, AlertTriangle } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { generateAITemplateRequest } from '../../../store/whatsappSlice';

export default function AIGeneratorCard() {
  const dispatch = useDispatch();
  const [prompt, setPrompt] = useState("");
  const textareaRef = useRef(null);

  const loading = useSelector((state) => state.whatsapp.aiGeneration?.loading);
  const error = useSelector((state) => state.whatsapp.aiGeneration?.error);

  const handleGenerateAI = () => {
    if (!prompt.trim() || loading) return;
    dispatch(generateAITemplateRequest({
      purpose: prompt,
      language: 'en',
      category: 'UTILITY'
    }));
  };

  return (
    <Card className="border-indigo-200 dark:border-indigo-900 shadow-sm bg-gradient-to-r from-indigo-50 to-transparent dark:from-indigo-950/30">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="p-2.5 bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-indigo-100 dark:border-indigo-800">
            <Sparkles className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Generate Template with AI</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Describe what you want to say in simple words. We'll write the professional template for you.</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 items-start">
              <textarea 
                ref={textareaRef}
                placeholder="e.g. Write a diwali offer message giving 20% discount..." 
                value={prompt}
                onChange={(e) => {
                  setPrompt(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                className="flex min-h-[40px] w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex-1 resize-none overflow-hidden"
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleGenerateAI();
                  }
                }}
                disabled={loading}
              />
              <Button 
                onClick={handleGenerateAI} 
                disabled={!prompt.trim() || loading}
                className="bg-indigo-600 hover:bg-indigo-700 text-white min-w-[120px] cursor-pointer disabled:opacity-50"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</>
                ) : (
                  <><Wand2 className="mr-2 h-4 w-4" /> Generate</>
                )}
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-300">Try:</span>
              <button 
                className="hover:text-indigo-600 dark:hover:text-indigo-400 underline decoration-dotted transition-colors" 
                onClick={() => setPrompt("Payment reminder message with amount and due date variables")}
              >
                Payment reminder
              </button>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <button 
                className="hover:text-indigo-600 dark:hover:text-indigo-400 underline decoration-dotted transition-colors" 
                onClick={() => setPrompt("Diwali sale announcement offering flat discount")}
              >
                Festival offer
              </button>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <button 
                className="hover:text-indigo-600 dark:hover:text-indigo-400 underline decoration-dotted transition-colors" 
                onClick={() => setPrompt("Welcome message to new user joining our platform")}
              >
                Welcome message
              </button>
            </div>

            {error && (
              <div className="mt-2 flex items-start gap-2 p-2 rounded bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
